import { Button, IngotModal } from "@/ingot";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useSubmitCartRfq } from "@/api/queries";
import { ApiError } from "@/lib/api";

/**
 * KAN-1754 — the cart fits into no shipping band, so it cannot be ordered
 * with standard shipping; one step turns it into a price enquiry instead.
 *
 * Nothing new is built here: the enquiry is ``POST /cart/rfq`` (KAN-302),
 * the whole cart becomes a draft quote the shop prices by hand, now with
 * ``reason="shipping_does_not_fit"`` so the shop's note says why. The
 * shop's manual loop — price, issue, customer accepts and pays — exists
 * and is only entered through one more door.
 *
 * The message names no carrier and no dimension: which band refused
 * which part is shop data, and the shop sees the parts in the quote.
 */
export function ShippingDoesNotFitGate({
  lang,
  defaultEmail,
  testId = "shipping-does-not-fit",
  pickupAvailable = false,
  inCart = false,
}: {
  /** URL language segment — for the redirect to ``/{lang}/quotes/by-id/…``. */
  lang: string;
  defaultEmail?: string | null;
  testId?: string;
  /**
   * KAN-1903 — the shop also offers in-person pickup. Then "cannot be
   * ordered" is not true: only the carriers are out, and the lead says so.
   */
  pickupAvailable?: boolean;
  /**
   * KAN-2015 — košík ještě nemá volbu doručení: odběr se zvolí v pokladně,
   * tak to věta řekne (pokladna má „zvolte osobní odběr" přímo u volby).
   */
  inCart?: boolean;
}): JSX.Element {
  const { t } = useTranslation("cart");
  const navigate = useNavigate();
  const rfq = useSubmitCartRfq();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setErrorCode(null);
    rfq.mutate(
      {
        name: name || undefined,
        email,
        phone: phone || undefined,
        message: message || undefined,
        reason: "shipping_does_not_fit",
      },
      {
        onSuccess: (data) => navigate(`/${lang}/quotes/by-id/${data.public_id}`),
        onError: (err) => setErrorCode(err instanceof ApiError ? err.code : "unknown"),
      },
    );
  };

  const field = "w-full rounded-md border border-border bg-surface px-3 py-2 text-sm";

  return (
    <div
      className="rounded-md border border-warn-border bg-warn-bg p-3 text-sm"
      data-testid={testId}
    >
      <p className="text-warn">
        {t(
          pickupAvailable
            ? inCart
              ? "shippingFit.leadWithPickupCart"
              : "shippingFit.leadWithPickup"
            : "shippingFit.lead",
        )}
      </p>
      <Button
        variant="accent"
        size="sm"
        className="mt-2"
        onClick={() => setOpen(true)}
        data-testid={`${testId}-open`}
      >
        {t("shippingFit.cta")}
      </Button>
      {open && (
        <IngotModal
          title={t("shippingFit.title")}
          closeLabel={t("shippingFit.close")}
          width={480}
          onClose={() => setOpen(false)}
          testId={`${testId}-dialog`}
        >
          <form className="space-y-3" onSubmit={submit}>
            <p className="text-sm text-ink-2">{t("shippingFit.formLead")}</p>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("shippingFit.name")}
              aria-label={t("shippingFit.name")}
              className={field}
              data-testid={`${testId}-name`}
            />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("shippingFit.email")}
              aria-label={t("shippingFit.email")}
              className={field}
              data-testid={`${testId}-email`}
            />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t("shippingFit.phone")}
              aria-label={t("shippingFit.phone")}
              className={field}
              data-testid={`${testId}-phone`}
            />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t("shippingFit.message")}
              aria-label={t("shippingFit.message")}
              rows={3}
              className={field}
              data-testid={`${testId}-message`}
            />
            {errorCode && (
              <p className="text-sm text-danger" data-testid={`${testId}-error`}>
                {t("shippingFit.error", { code: errorCode })}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>
                {t("shippingFit.cancel")}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={rfq.isPending || !email}
                data-testid={`${testId}-submit`}
              >
                {t("shippingFit.submit")}
              </Button>
            </div>
          </form>
        </IngotModal>
      )}
    </div>
  );
}
